import {
  ChatTransport,
  ChatTransportEvent,
  ChatTransportListener,
} from "@/types";

/** Default poll cadence in milliseconds. */
const DEFAULT_INTERVAL_MS = 3000;

/**
 * Create a thin timer-based transport for live chat.
 *
 * On `connect()` it emits a `changed` event on a fixed interval; the session
 * store responds by re-fetching authoritative state from the poll endpoint. The
 * transport itself performs no network calls and holds no conversation state,
 * which keeps it swappable for a future push-based (WebSocket / Firebase)
 * transport that would emit `changed` on real server events.
 */
export function createPollingTransport(options?: {
  intervalMs?: number;
}): ChatTransport {
  const intervalMs = options?.intervalMs ?? DEFAULT_INTERVAL_MS;
  const listeners = new Set<ChatTransportListener>();
  let timer: ReturnType<typeof setInterval> | null = null;

  const emit = (event: ChatTransportEvent): void => {
    listeners.forEach((listener) => listener(event));
  };

  const connect = (): void => {
    const alreadyConnected = timer !== null;
    if (!alreadyConnected) {
      emit({ type: "connected" });
      timer = setInterval(() => {
        emit({ type: "changed" });
      }, intervalMs);
    }
  };

  const disconnect = (): void => {
    const isConnected = timer !== null;
    if (isConnected) {
      clearInterval(timer as ReturnType<typeof setInterval>);
      timer = null;
      emit({ type: "disconnected" });
    }
  };

  const subscribe = (listener: ChatTransportListener): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  return { connect, disconnect, subscribe };
}
